import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Loader2, Lock, Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead, EASE } from "./Reveal";
import { API, http, formatApiError } from "../lib/api";
import { CONTACT } from "../content";

const ROLE_LEVELS = ["CXO / C-Suite", "Senior Management", "Mid Management", "Advisory / Board"];
const SECTORS = ["Banking", "NBFC", "Insurance", "Payments", "Fintech", "Mutual Funds", "Stock Broking", "Startup", "Others"];
const EMPTY = { name: "", designation: "", company: "", email: "", phone: "", role_level: "", sector: "", location: "", positions: 1, confidential: false, message: "" };

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">{label}</span>
    {children}
  </label>
);

const ContactAside = () => (
  <div className="lg:col-span-5">
    <SectionHead
      index="08"
      label="Let's build winning teams"
      title={<>Brief us on your next <em className="text-emerald-400">leadership</em> mandate.</>}
      sub="Share the role and we'll come back quick and sharp with an initial view on the market, talent availability and approach."
    />
    <Reveal delay={0.2} className="mt-12 space-y-5">
      {[
        [Mail, CONTACT.email, "contact-email"],
        [Phone, CONTACT.phone, "contact-phone"],
        [MapPin, CONTACT.address, "contact-address"],
      ].map(([Icon, v, id]) => (
        <p key={id} className="flex items-center gap-4 text-zinc-200" data-testid={id}>
          <span className="grid h-10 w-10 place-items-center rounded-full border border-white/15">
            <Icon className="h-4 w-4 text-emerald-400" />
          </span>
          {v}
        </p>
      ))}
    </Reveal>
    <Reveal delay={0.3} className="mt-12 flex items-start gap-4 rounded-md border border-white/10 bg-[#111519] p-5">
      <Lock className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
      <p className="text-sm text-zinc-300 leading-relaxed">
        Every brief is treated as confidential. Mark it as a confidential mandate and we'll run the search without
        disclosing your organisation until you approve.
      </p>
    </Reveal>
  </div>
);

export const MandateForm = () => {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.role_level || !f.sector) {
      toast.error("Please select the role level and sector.");
      return;
    }
    setLoading(true);
    try {
      await http.post(`${API}/enquiries`, { ...f, positions: Number(f.positions) || 1 });
      setDone(true);
      setF(EMPTY);
      toast.success("Mandate received. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="mandate" className="relative py-28 md:py-40 bg-[#0d1013] border-t border-white/10" data-testid="mandate-section">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <ContactAside />
        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="relative rounded-md border border-white/10 bg-[#0a0c0e] p-6 sm:p-10">
            <AnimatePresence initial={false}>
              {done ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="flex min-h-[520px] flex-col items-start justify-center"
                  data-testid="mandate-success"
                >
                  <CheckCircle2 className="h-12 w-12 text-emerald-400" strokeWidth={1.3} />
                  <p className="font-display mt-6 text-5xl leading-none">Thank you.</p>
                  <p className="mt-4 max-w-md text-zinc-300">Your mandate has reached our search team. A partner will reach out within one business day.</p>
                  <button onClick={() => setDone(false)} className="mt-10 text-sm text-emerald-300 underline underline-offset-4" data-testid="mandate-new-btn">
                    Submit another mandate
                  </button>
                </motion.div>
              ) : (
                <motion.form key="form" onSubmit={submit} className="grid grid-cols-1 gap-x-8 gap-y-7 sm:grid-cols-2" data-testid="mandate-form">
                  <Field label="Your name *"><input required className="field" value={f.name} onChange={set("name")} placeholder="Full name" data-testid="mandate-form-name-input" /></Field>
                  <Field label="Designation"><input className="field" value={f.designation} onChange={set("designation")} placeholder="e.g. CHRO, Talent Head" data-testid="mandate-form-designation-input" /></Field>
                  <Field label="Company *"><input required className="field" value={f.company} onChange={set("company")} placeholder="Organisation name" data-testid="mandate-form-company-input" /></Field>
                  <Field label="Official email *"><input required type="email" className="field" value={f.email} onChange={set("email")} placeholder="name@company.com" data-testid="mandate-form-email-input" /></Field>
                  <Field label="Phone *"><input required className="field" value={f.phone} onChange={set("phone")} placeholder="+91" data-testid="mandate-form-phone-input" /></Field>
                  <Field label="Role location"><input className="field" value={f.location} onChange={set("location")} placeholder="e.g. Mumbai, Pan-India" data-testid="mandate-form-location-input" /></Field>
                  <Field label="Role level *">
                    <select required className="field" value={f.role_level} onChange={set("role_level")} data-testid="mandate-form-role-select">
                      <option value="" disabled>Select level</option>
                      {ROLE_LEVELS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </Field>
                  <Field label="Sector *">
                    <select required className="field" value={f.sector} onChange={set("sector")} data-testid="mandate-form-sector-select">
                      <option value="" disabled>Select sector</option>
                      {SECTORS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </Field>
                  <Field label="No. of positions"><input type="number" min="1" max="500" className="field" value={f.positions} onChange={set("positions")} data-testid="mandate-form-positions-input" /></Field>
                  <label className="flex items-end gap-3 pb-3 cursor-pointer select-none" data-testid="mandate-form-confidential-label">
                    <input type="checkbox" checked={f.confidential} onChange={set("confidential")} className="h-4 w-4 accent-emerald-500" data-testid="mandate-form-confidential-checkbox" />
                    <span className="text-sm text-zinc-300">This is a confidential mandate</span>
                  </label>
                  <Field label="Brief" className="sm:col-span-2">
                    <textarea rows={4} className="field resize-none" value={f.message} onChange={set("message")} placeholder="Role, reporting line, key outcomes, timelines..." data-testid="mandate-form-message-input" />
                  </Field>
                  <div className="sm:col-span-2 flex flex-col-reverse gap-6 pt-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs text-zinc-400">By submitting, you agree to be contacted by PLUK HR about this mandate.</p>
                    <button
                      type="submit"
                      disabled={loading}
                      className="group inline-flex items-center justify-center gap-3 rounded-full bg-emerald-500 pl-6 pr-2 py-2 text-[15px] font-medium text-[#0a0c0e] transition-colors hover:bg-emerald-400 disabled:opacity-60"
                      data-testid="mandate-form-submit-btn"
                    >
                      {loading ? "Sending" : "Submit mandate"}
                      <span className="grid h-10 w-10 place-items-center rounded-full bg-[#0a0c0e] text-emerald-400 transition-transform duration-500 group-hover:rotate-45">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                      </span>
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
